import React, { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Printer, CreditCard, Mail, Send, ImageIcon, Download, Copy, Check, MessageCircle, Package, Truck, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "../utils/api";
import { formatMXN, formatDate, typeLabels, statusLabels, statusColors } from "../utils/format";

export default function SaleDetail({ sale: initialSale, onClose, onRefresh }) {
  const [sale, setSale] = useState(initialSale);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("efectivo");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState(initialSale.customer_email || "");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [generatingImg, setGeneratingImg] = useState(false);
  const [generatedImg, setGeneratedImg] = useState(null);
  const [copied, setCopied] = useState(false);
  const [trackingCarrier, setTrackingCarrier] = useState(initialSale.tracking_carrier || "");
  const [trackingNumber, setTrackingNumber] = useState(initialSale.tracking_number || "");
  const [savingTracking, setSavingTracking] = useState(false);
  const [actualShipCost, setActualShipCost] = useState(String(initialSale.actual_shipping_cost || ""));
  const [savingShipCost, setSavingShipCost] = useState(false);
  const printRef = useRef();

  const handlePrint = () => {
    if (!printRef.current) return;
    const printWindow = window.open("", "_blank");
    if (!printWindow) { toast.error("Permite ventanas emergentes para imprimir"); return; }
    const content = printRef.current.innerHTML;
    printWindow.document.write(`
      <!DOCTYPE html><html><head><title>${sale.folio || "Documento"}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Inter, Arial, sans-serif; padding: 10px; background: #fff; }
        @media print { body { padding: 0; } @page { margin: 10mm; } }
      </style></head>
      <body>${content}</body></html>
    `);
    printWindow.document.close();
    printWindow.onload = () => { printWindow.focus(); printWindow.print(); };
  };

  const handleGenerateImage = async () => {
    setGeneratingImg(true);
    try {
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
      });
      const dataUrl = canvas.toDataURL("image/png");
      setGeneratedImg(dataUrl);
    } catch (err) {
      toast.error("Error al generar imagen");
    } finally {
      setGeneratingImg(false);
    }
  };

  const handleDownloadImg = () => {
    if (!generatedImg) return;
    const a = document.createElement("a");
    a.href = generatedImg;
    a.download = `${typeLabels[sale.type]}-${sale.folio}.png`;
    a.click();
    toast.success("Imagen descargada");
  };

  const handleCopyImg = async () => {
    if (!generatedImg) return;
    try {
      const res = await fetch(generatedImg);
      const blob = await res.blob();
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      setCopied(true);
      toast.success("Imagen copiada — pégala en tu chat (Ctrl+V)");
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // Fallback: open in new tab for manual copy
      const w = window.open();
      w.document.write(`<img src="${generatedImg}" style="max-width:100%"/>`);
      toast.info("Se abrió en nueva pestaña — clic derecho → Copiar imagen");
    }
  };

  const handleSendEmail = async () => {
    if (!emailTo) { toast.error("Ingresa un correo destinatario"); return; }
    setSendingEmail(true);
    try {
      await api.post(`/sales/${sale.id}/send-email`, { email: emailTo });
      toast.success(`Correo enviado a ${emailTo}`);
      setShowEmailModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar correo");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleAddPayment = async () => {
    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) { toast.error("Ingresa un monto válido"); return; }
    try {
      const res = await api.post(`/sales/${sale.id}/payment`, { amount, method: paymentMethod });
      setSale(res.data);
      setPaymentAmount("");
      toast.success("Abono registrado");
      onRefresh();
    } catch (err) {
      toast.error("Error al registrar abono");
    }
  };

  const handleSaveTracking = async () => {
    if (!trackingCarrier || !trackingNumber.trim()) { toast.error("Selecciona paquetería e ingresa número de guía"); return; }
    setSavingTracking(true);
    try {
      const res = await api.put(`/sales/${sale.id}`, { tracking_carrier: trackingCarrier, tracking_number: trackingNumber.trim() });
      setSale(res.data);
      toast.success("Guía de rastreo guardada");
      onRefresh();
    } catch { toast.error("Error al guardar guía"); }
    finally { setSavingTracking(false); }
  };

  const handleShareTracking = () => {
    const phone = cleanPhone(sale.customer_phone);
    if (!phone) { toast.error("Cliente sin teléfono registrado"); return; }
    const carrierNames = { fedex: "FedEx", dhl: "DHL", paquetexpress: "Paquetexpress" };
    const carrierName = carrierNames[sale.tracking_carrier] || sale.tracking_carrier;
    const msg = encodeURIComponent(
      `Hola ${sale.customer_name || ""}, tu pedido ${sale.folio} ha sido enviado por ${carrierName}.\n\nNo. de guía: ${sale.tracking_number}\n\nRastrea tu paquete aquí:\n${sale.tracking_url}`
    );
    const wa = phone.startsWith("52") ? phone : `52${phone}`;
    window.open(`https://web.whatsapp.com/send?phone=${wa}&text=${msg}`, "_blank");
  };

  const paid = (sale.payments || []).reduce((a, p) => a + (p.amount || 0), 0);
  const pending = sale.total - paid;
  const [company, setCompany] = React.useState({});

  React.useEffect(() => {
    api.get("/company").then((r) => setCompany(r.data)).catch(() => {});
  }, []);

  const cleanPhone = (phone) => (phone || "").replace(/\D/g, "");

  const handleWhatsApp = async () => {
    const phone = cleanPhone(sale.customer_phone);
    if (!phone) { toast.error("Este cliente no tiene teléfono registrado"); return; }
    toast.info("Generando imagen...");
    try {
      // 1. Generate image
      const canvas = await (await import("html2canvas")).default(printRef.current, {
        scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false
      });
      // 2. Copy to clipboard
      canvas.toBlob(async (blob) => {
        try {
          await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
          // 3. Open WhatsApp Web
          const msg = encodeURIComponent(`Hola, te comparto el documento ${typeLabels[sale.type]} ${sale.folio}.`);
          const wa = phone.startsWith("52") ? phone : `52${phone}`;
          window.open(`https://web.whatsapp.com/send?phone=${wa}&text=${msg}`, "_blank");
          toast.success("Imagen copiada — cuando abra el chat, presiona Ctrl+V para pegarla", { duration: 8000 });
        } catch {
          // Fallback: download the image
          const url = canvas.toDataURL("image/png");
          const a = document.createElement("a");
          a.href = url; a.download = `${typeLabels[sale.type]}-${sale.folio}.png`; a.click();
          const msg = encodeURIComponent(`Hola, te comparto el documento ${typeLabels[sale.type]} ${sale.folio}.`);
          const wa = phone.startsWith("52") ? phone : `52${phone}`;
          window.open(`https://web.whatsapp.com/send?phone=${wa}&text=${msg}`, "_blank");
          toast.info("Imagen descargada — adjúntala en WhatsApp con el ícono de clip 📎", { duration: 8000 });
        }
      }, "image/png");
    } catch {
      toast.error("Error al generar imagen");
    }
  };

  return (
    <>
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="sale-detail">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>{typeLabels[sale.type]} — {sale.folio}</DialogTitle>
            <div className="flex gap-2 flex-wrap">
              {sale.customer_phone && (
                <button onClick={handleWhatsApp}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg text-sm transition-colors font-medium"
                  data-testid="whatsapp-btn">
                  <MessageCircle size={14} /> WhatsApp
                </button>
              )}
              <button onClick={() => setShowEmailModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-sm transition-colors"
                data-testid="send-email-btn">
                <Mail size={14} /> Correo
              </button>
              <button onClick={handleGenerateImage} disabled={generatingImg}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 hover:bg-violet-100 text-violet-700 rounded-lg text-sm transition-colors disabled:opacity-60"
                data-testid="generate-image-btn">
                <ImageIcon size={14} />
                {generatingImg ? "Generando..." : "Imagen"}
              </button>
              <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm transition-colors">
                <Printer size={14} /> Imprimir
              </button>
            </div>
          </div>
        </DialogHeader>

        {/* Printable area */}
        <div ref={printRef} className="print:p-6" style={{ background: '#fff' }}>
          <div style={{ border: '2px solid #2D4A3E', borderRadius: 12, overflow: 'hidden', fontFamily: 'Inter, Arial, sans-serif' }}>

            {/* ── HEADER BAND ── */}
            <div style={{ background: '#2D4A3E', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {/* Logo + company */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {company.logo
                  ? <img src={company.logo} alt="logo" style={{ height: 52, width: 52, objectFit: 'contain', borderRadius: 8, background: '#fff', padding: 3 }} />
                  : <div style={{ width: 52, height: 52, borderRadius: 8, border: '2px dashed #7BA899', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: 9, color: '#B0A89C', textAlign: 'center', lineHeight: 1.2 }}>TU<br/>LOGO</span>
                    </div>
                }
                <div>
                  <div style={{ color: '#fff', fontSize: 17, fontWeight: 800, lineHeight: 1.1 }}>
                    {company.name || 'Mi Empresa'}
                  </div>
                  {company.tagline && (
                    <div style={{ color: '#C5BDB1', fontSize: 11, marginTop: 3, fontStyle: 'italic' }}>{company.tagline}</div>
                  )}
                  {company.phone && <div style={{ color: '#B0A89C', fontSize: 11, marginTop: 2 }}>{company.phone}</div>}
                </div>
              </div>
              {/* Doc type + folio */}
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#7BA899', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
                  {typeLabels[sale.type]}
                </div>
                <div style={{ color: '#fff', fontSize: 22, fontWeight: 800, lineHeight: 1.1 }}>{sale.folio}</div>
                <div style={{ color: '#C5BDB1', fontSize: 12, marginTop: 6 }}>Fecha: {formatDate(sale.created_at)}</div>
                {sale.valid_until && sale.type === 'cotizacion' && (
                  <div style={{ color: '#C4A35A', fontSize: 11, marginTop: 3 }}>Válido hasta: {formatDate(sale.valid_until)}</div>
                )}
                {sale.show_queue_position !== false && !["enviado", "entregado", "cancelado"].includes(sale.status) && (sale.type === 'pedido' || sale.type === 'importada') && (
                  <div style={{ color: '#B8D4C8', fontSize: 12, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontWeight: 700 }}>
                      Posición en fila: #{sale.queue_position ? sale.queue_position + 4 : 4}
                    </span>
                    <span style={{ color: '#7BA899', fontSize: 10 }}>
                      ({sale.queue_position ? sale.queue_position + 3 : 3} pedido(s) antes del tuyo)
                    </span>
                  </div>
                )}
                <div style={{
                  display: 'inline-block', marginTop: 6,
                  background: sale.status === 'pagado' ? '#7BA899' : sale.status === 'cancelado' ? '#C75C5C' : '#C4A35A',
                  color: '#fff', borderRadius: 20, padding: '3px 10px',
                  fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em'
                }}>
                  {statusLabels[sale.status] || sale.status}
                </div>
              </div>
            </div>

            {/* ── COMPANY + CUSTOMER ROW ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1.5px solid #DCDCDA' }}>
              <div style={{ padding: '14px 24px', borderRight: '1.5px solid #DCDCDA', background: '#F4EFE6' }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#7BA899', marginBottom: 5 }}>Emisor</div>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#2C2C2C' }}>{company.name || 'Mi Empresa'}</div>
                {company.rfc && <div style={{ fontSize: 12, color: '#6B665E', marginTop: 1 }}>RFC: {company.rfc}</div>}
                {company.address && <div style={{ fontSize: 12, color: '#6B665E' }}>{company.address}</div>}
                {company.email && <div style={{ fontSize: 12, color: '#6B665E' }}>{company.email}</div>}
              </div>
              <div style={{ padding: '14px 24px', background: '#F4EFE6' }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#2D4A3E', marginBottom: 5 }}>Cliente</div>
                {sale.customer_name
                  ? <>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#2C2C2C' }}>{sale.customer_name}</div>
                      {sale.customer_rfc && <div style={{ fontSize: 12, color: '#6B665E', marginTop: 1 }}>RFC: {sale.customer_rfc}</div>}
                      {sale.customer_phone && <div style={{ fontSize: 12, color: '#6B665E' }}>Tel: {sale.customer_phone}</div>}
                      {sale.customer_address && <div style={{ fontSize: 12, color: '#6B665E' }}>{sale.customer_address}</div>}
                    </>
                  : <div style={{ fontSize: 13, color: '#8A8478', fontStyle: 'italic' }}>Venta de mostrador</div>
                }
              </div>
            </div>

            {/* ── PRODUCTS TABLE ── */}
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 0.7fr 1fr', background: '#2D4A3E', padding: '10px 24px', gap: 8 }}>
                {['Producto', 'Cantidad', 'Precio unit.', 'Desc.', 'Subtotal'].map((h, i) => (
                  <div key={h} style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#C5BDB1', textAlign: i > 0 ? 'right' : 'left' }}>{h}</div>
                ))}
              </div>
              {(sale.items || []).map((item, i) => (
                <div key={i} style={{
                  display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 0.7fr 1fr',
                  padding: '12px 24px', gap: 8,
                  background: i % 2 === 0 ? '#FFFDF8' : '#F4EFE6',
                  borderBottom: '1px solid #DCDCDA'
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#2C2C2C' }}>{item.product_name}</div>
                  <div style={{ fontSize: 13, color: '#2C2C2C', textAlign: 'right' }}>{item.quantity} {item.unit}</div>
                  <div style={{ fontSize: 13, color: '#2C2C2C', textAlign: 'right' }}>{formatMXN(item.unit_price)}</div>
                  <div style={{ fontSize: 13, color: item.discount > 0 ? '#C75C5C' : '#8A8478', textAlign: 'right' }}>{item.discount > 0 ? `${item.discount}%` : '—'}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#2C2C2C', textAlign: 'right' }}>{formatMXN(item.subtotal)}</div>
                </div>
              ))}
            </div>

            {/* ── TOTALS + NOTES ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderTop: '1.5px solid #DCDCDA' }}>
              <div style={{ padding: '16px 24px', borderRight: '1.5px solid #DCDCDA', background: '#F4EFE6' }}>
                {sale.notes
                  ? <><div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#8A8478', marginBottom: 6 }}>Notas</div>
                      <div style={{ fontSize: 13, color: '#5A5650', lineHeight: 1.5 }}>{sale.notes}</div></>
                  : <div style={{ fontSize: 12, color: '#C5BEB5', fontStyle: 'italic' }}>Sin notas adicionales</div>
                }
              </div>
              <div style={{ padding: '16px 24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6B665E', marginBottom: 6 }}>
                  <span>Subtotal</span><span>{formatMXN(sale.subtotal)}</span>
                </div>
                {sale.discount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#C75C5C', marginBottom: 6 }}>
                    <span>Descuento ({sale.discount}%)</span><span>-{formatMXN(sale.subtotal * sale.discount / 100)}</span>
                  </div>
                )}
                {sale.tax_amount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6B665E', marginBottom: 6 }}>
                    <span>IVA ({sale.tax_rate}%)</span><span>{formatMXN(sale.tax_amount)}</span>
                  </div>
                )}
                {(sale.shipping_cost > 0) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#C4A35A', marginBottom: 6 }}>
                    <span>Envío</span><span>{formatMXN(sale.shipping_cost)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid #2D4A3E', paddingTop: 8, marginTop: 4 }}>
                  <span style={{ fontSize: 15, fontWeight: 800, color: '#2C2C2C' }}>TOTAL</span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: '#7BA899' }}>{formatMXN(sale.total)}</span>
                </div>
                {paid > 0 && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#7BA899', marginTop: 6 }}>
                      <span>Pagado</span><span>{formatMXN(paid)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, marginTop: 4, color: pending > 0 ? '#C75C5C' : '#7BA899' }}>
                      <span>{pending > 0 ? 'Saldo pendiente' : 'Liquidado'}</span>
                      <span>{pending > 0 ? formatMXN(pending) : '✓'}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ── TRACKING INFO (in document) ── */}
            {sale.tracking_number && sale.tracking_carrier && (
              <div style={{ padding: '10px 24px', borderTop: '1px solid #DCDCDA', background: '#F4EFE6', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#2D4A3E', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Guía {({fedex:'FedEx',dhl:'DHL',paquetexpress:'Paquetexpress'})[sale.tracking_carrier] || sale.tracking_carrier}:
                </span>
                <span style={{ fontSize: 12, color: '#2C2C2C', fontWeight: 600 }}>{sale.tracking_number}</span>
                {sale.tracking_url && (
                  <a href={sale.tracking_url} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 11, color: '#7BA899', textDecoration: 'underline', marginLeft: 4 }}>
                    Rastrear paquete
                  </a>
                )}
              </div>
            )}

            {/* ── FOOTER ── */}
            <div style={{ background: '#F0EAE0', borderTop: '1.5px solid #DCDCDA', padding: '10px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#8A8478' }}>
                {company.doc_footer || `${company.name || 'RasputinERP'} · ${company.email || ''}`}
              </div>
            </div>

          </div>
        </div>


        {/* ── TRACKING SECTION (not printed) ── */}
        {(sale.type === "pedido" || sale.type === "importada") && sale.status !== "cancelado" && (
          <div className="print:hidden border-t border-slate-200 pt-4 mt-2" data-testid="tracking-section">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Truck size={15} /> Rastreo de paquetería
            </h3>
            <div className="flex gap-3 items-end flex-wrap">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Paquetería</label>
                <select value={trackingCarrier} onChange={(e) => setTrackingCarrier(e.target.value)}
                  data-testid="tracking-carrier-select"
                  className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500 w-40">
                  <option value="">Seleccionar...</option>
                  <option value="fedex">FedEx</option>
                  <option value="dhl">DHL</option>
                  <option value="paquetexpress">Paquetexpress</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">No. de Guía</label>
                <input type="text" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)}
                  data-testid="tracking-number-input"
                  className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500 w-52"
                  placeholder="Ej: 7945 6890 1234" />
              </div>
              <button onClick={handleSaveTracking} disabled={savingTracking}
                data-testid="save-tracking-btn"
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-60">
                {savingTracking ? "Guardando..." : "Guardar guía"}
              </button>
            </div>
            {sale.tracking_url && (
              <div className="mt-3 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5" data-testid="tracking-info">
                <Package size={16} className="text-amber-600" />
                <a href={sale.tracking_url} target="_blank" rel="noopener noreferrer"
                  className="text-sm text-amber-800 hover:text-amber-900 underline flex items-center gap-1 font-medium"
                  data-testid="tracking-link">
                  Rastrear en {({fedex:'FedEx',dhl:'DHL',paquetexpress:'Paquetexpress'})[sale.tracking_carrier] || sale.tracking_carrier}
                  <ExternalLink size={12} />
                </a>
                {sale.customer_phone && (
                  <button onClick={handleShareTracking}
                    data-testid="share-tracking-whatsapp"
                    className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg text-xs font-medium transition-colors">
                    <MessageCircle size={13} /> Compartir por WhatsApp
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {sale.shipping_cost > 0 && (
          <div className="print:hidden border-t border-slate-200 pt-4 mt-2" data-testid="actual-shipping-section">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Package size={15} /> Costo Real de Envio
            </h3>
            <div className="bg-slate-50 rounded-lg p-3 mb-3">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-xs text-slate-500">Cobrado al cliente</div>
                  <div className="text-sm font-bold text-slate-900">{formatMXN(sale.shipping_cost)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Costo real</div>
                  <div className="text-sm font-bold text-slate-900">
                    {sale.actual_shipping_cost > 0 ? formatMXN(sale.actual_shipping_cost) : "Sin registrar"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Diferencia</div>
                  <div className={"text-sm font-bold " + (sale.actual_shipping_cost > 0 ? ((sale.shipping_cost - sale.actual_shipping_cost) >= 0 ? "text-emerald-600" : "text-red-600") : "text-slate-400")}>
                    {sale.actual_shipping_cost > 0 ? ((sale.shipping_cost - sale.actual_shipping_cost) >= 0 ? "+" : "") + formatMXN(sale.shipping_cost - sale.actual_shipping_cost) : "-"}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-3 items-end">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Costo real del envio ($)</label>
                <input type="number" step="0.01" value={actualShipCost}
                  onChange={(e) => setActualShipCost(e.target.value)}
                  data-testid="actual-shipping-cost-input"
                  className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500 w-40"
                  placeholder="0.00" />
              </div>
              <button disabled={savingShipCost} data-testid="save-actual-shipping-btn"
                onClick={async () => {
                  var val = parseFloat(actualShipCost);
                  if (isNaN(val) || val < 0) { toast.error("Ingresa un monto valido"); return; }
                  setSavingShipCost(true);
                  try {
                    var res = await api.put("/sales/" + sale.id, { actual_shipping_cost: val });
                    setSale(res.data);
                    toast.success("Costo real de envio guardado");
                    if (onRefresh) onRefresh();
                  } catch (err) { toast.error("Error al guardar"); }
                  setSavingShipCost(false);
                }}
                className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-60">
                {savingShipCost ? "Guardando..." : "Guardar costo real"}
              </button>
            </div>
          </div>
        )}

        {/* Payments section (not printed) */}
        <div className="print:hidden border-t border-slate-200 pt-4 mt-2">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <CreditCard size={15} /> Registrar abono
          </h3>
          <div className="flex gap-3 items-end">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Monto</label>
              <input type="number" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500 w-36"
                placeholder="0.00" min="0" step="0.01" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Método</label>
              <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500">
                {["efectivo", "transferencia", "tarjeta", "cheque", "otro"].map((m) => (
                  <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
                ))}
              </select>
            </div>
            <button onClick={handleAddPayment} data-testid="add-payment-btn"
              className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-md text-sm font-medium transition-colors">
              Agregar abono
            </button>
          </div>

          {/* Payment history */}
          {(sale.payments || []).length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold text-slate-500 mb-2">Historial de pagos</div>
              <div className="space-y-1">
                {sale.payments.map((p) => (
                  <div key={p.id} className="flex justify-between text-xs text-slate-600 bg-slate-50 px-3 py-1.5 rounded">
                    <span>{formatDate(p.date)} — {p.method}</span>
                    <span className="font-medium text-brand-700">{formatMXN(p.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>

    {/* Image preview modal */}
    {generatedImg && (
      <Dialog open onOpenChange={() => setGeneratedImg(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="image-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon size={18} className="text-violet-600" />
              Imagen del documento — {typeLabels[sale.type]} {sale.folio}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Preview */}
            <div className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50 p-2">
              <img src={generatedImg} alt="Documento" className="w-full rounded-lg shadow-sm" />
            </div>
            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleCopyImg}
                data-testid="copy-image-btn"
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-colors bg-violet-600 hover:bg-violet-700 text-white"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? "¡Copiada!" : "Copiar imagen (Ctrl+V en tu chat)"}
              </button>
              <button
                onClick={handleDownloadImg}
                data-testid="download-image-btn"
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold transition-colors bg-slate-100 hover:bg-slate-200 text-slate-700"
              >
                <Download size={16} /> Descargar
              </button>
            </div>
            <p className="text-xs text-slate-400 text-center">
              Copia la imagen y pégala directamente en WhatsApp, Telegram, Messenger o cualquier chat
            </p>
          </div>
        </DialogContent>
      </Dialog>
    )}

    {/* Email modal */}
    {showEmailModal && (
      <Dialog open onOpenChange={() => setShowEmailModal(false)}>
        <DialogContent className="max-w-md" data-testid="email-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail size={18} className="text-blue-600" />
              Enviar por correo electrónico
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <span className="text-slate-500">Documento: </span>
              <span className="font-semibold text-slate-800">{typeLabels[sale.type]} {sale.folio}</span>
              <br />
              <span className="text-slate-500">Cliente: </span>
              <span className="font-medium text-slate-700">{sale.customer_name || "Sin cliente"}</span>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                Correo del destinatario
              </label>
              <input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                data-testid="email-recipient-input"
                className="w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="cliente@correo.com"
                autoFocus
              />
              <p className="text-xs text-slate-400 mt-1.5">
                Se enviará el documento completo en formato HTML con todos los detalles.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowEmailModal(false)}
              className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
              Cancelar
            </button>
            <button onClick={handleSendEmail} disabled={sendingEmail}
              data-testid="confirm-send-email-btn"
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60">
              <Send size={14} />
              {sendingEmail ? "Enviando..." : "Enviar correo"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
}
